/**
 * What the ad networks refuse, checked at the point of entry (MPN-1616).
 *
 * These restrictions were only ever discovered at publish, when the network rejected the
 * content and the work had to be redone. Nothing here blocks anything: the rules warn,
 * the text stays editable, and the copy is saved either way. A field can break more than
 * one rule at once and says so.
 *
 * Pure functions with no React in them, because the interesting part is which strings
 * trip which rule, and that should be testable without rendering anything.
 */

export type AdCopyField = "copy_text" | "link_headline" | "link_description" | "cta_text";

export const EMOJI_OR_PHONE =
  "Google does not accept emojis or phone numbers. Remove to avoid errors on publish to Google.";
export const BODY_COPY_TOO_LONG =
  "Google limits display to 90 characters. Ensure the first 90 characters are a complete sentence/phrase.";
export const HEADLINE_TOO_LONG =
  "Headlines for Snapchat and Google need to be 34 characters or less. Shorten text to avoid errors on publish.";
export const APOSTROPHE_IN_AUDIENCE_NAME =
  "Google does not accept apostrophes in Audience Names and will trigger an error on publish. " +
  "Remove to avoid errors on publish to Google.";

export const BODY_COPY_LIMIT = 90;
export const HEADLINE_LIMIT = 34;

/**
 * Anything the Unicode standard calls a pictograph.
 *
 * Extended_Pictographic rather than a hand-listed range: emoji are added with every
 * Unicode release, and a literal list is out of date the moment it ships. It also
 * deliberately covers characters people do not think of as emoji -- ™, ©, ▶ -- which is
 * correct here, since the question is what Google's parser rejects, not what looks like
 * an emoji to us.
 *
 * Regional_Indicator is a second alternative because flags are not pictographs. A flag
 * is two regional indicator letters -- U+1F1FA U+1F1F8 for the US -- and matches nothing
 * in Extended_Pictographic, so 🇺🇸 would have passed straight through to Google.
 */
const EMOJI = /\p{Extended_Pictographic}|\p{Regional_Indicator}/u;

/**
 * The formats named on the ticket, and the same ones behind a +1 or 1.
 *
 * Anchored on a digit boundary at both ends so a longer run of digits is not read as a
 * phone number hiding inside it -- an order reference or a tracking code would otherwise
 * warn on copy that is perfectly publishable.
 */
/**
 * A digit, or a placeholder standing in for one.
 *
 * Ad copy is written as a template and filled in later, so the number that reaches this
 * field is often "(###) ###-####" or "XXX-XXX-XXXX" rather than real digits. Those are
 * phone numbers to every reader, and if nobody substitutes them they publish exactly as
 * written -- which is worse than a real number, not better. Digits alone missed all of
 * them, which is what QA hit.
 */
const D = "[\\d#Xx]";

const PHONE_PATTERNS: RegExp[] = [
  // (XXX) XXX-XXXX, with or without the space
  new RegExp(`(?<![\\d#Xx])(?:\\+?1[\\s.-]?)?\\(${D}{3}\\)\\s?${D}{3}[-.\\s]${D}{4}(?![\\d#Xx])`),
  // XXX-XXX-XXXX and XXX.XXX.XXXX and XXX XXX XXXX
  new RegExp(`(?<![\\d#Xx])(?:\\+?1[\\s.-]?)?${D}{3}[-.\\s]${D}{3}[-.\\s]${D}{4}(?![\\d#Xx])`),
  // XXXXXXXXXX, and 1XXXXXXXXXX / +1XXXXXXXXXX
  new RegExp(`(?<![\\d#Xx])\\+?1?${D}{10}(?![\\d#Xx])`),
];

export const hasEmoji = (value: string): boolean => EMOJI.test(value ?? "");

export const hasPhoneNumber = (value: string): boolean =>
  PHONE_PATTERNS.some((pattern) => pattern.test(value ?? ""));

/**
 * Every rule this field breaks, in the order they should be read.
 *
 * A list rather than a first-match, because the ticket asks for all applicable messages
 * together: a headline can be both too long and contain an emoji, and fixing one would
 * otherwise reveal the other one error at a time.
 */
export function adCopyIssues(field: AdCopyField, value: string): string[] {
  const text = value ?? "";
  const issues: string[] = [];

  if (hasEmoji(text) || hasPhoneNumber(text)) issues.push(EMOJI_OR_PHONE);
  if (field === "copy_text" && text.length > BODY_COPY_LIMIT) issues.push(BODY_COPY_TOO_LONG);
  if (field === "link_headline" && text.length > HEADLINE_LIMIT) issues.push(HEADLINE_TOO_LONG);

  return issues;
}

/** Apostrophes Google refuses, straight and curly -- a paste from Word carries the curly one. */
export function audienceNameIssues(value: string): string[] {
  return /['’]/.test(value ?? "") ? [APOSTROPHE_IN_AUDIENCE_NAME] : [];
}
